import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import MainLayout from '../layouts/main';
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: '/',
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
      children: [
        { path: 'verify', element: <VerifyCode /> },
        // { path: '*', element: <Navigate to="/login" replace /> }
      ],
    },
    { path: 'qr-form', element: <QROrderForm /> },
    { path: 'qr-form-places', element: <QROrderFormPlaces /> },
    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <GeneralApp /> },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/list" replace />, index: true },
            { path: 'profile', element: <UserProfile /> },
            { path: 'cards', element: <UserCards /> },
            { path: 'list', element: <UserList /> },
            { path: 'new', element: <UserCreate /> },
            { path: ':id/edit', element: <UserCreate /> },
            { path: 'account', element: <UserAccount /> },
          ],
        },
        {
          path: 'config',
          children: [
            { element: <Navigate to="/dashboard/config" replace />, index: true },
            { path: 'modify', element: <GlobalConfig /> },
          ],
        },
        {
          path: 'profile',
          children: [
            { element: <Navigate to="/dashboard/profile/list" replace />, index: true },
            { path: 'list', element: <ProfileList /> },
            { path: 'new', element: <ProfileCreate /> },
            { path: ':id/edit', element: <ProfileCreate /> },
          ],
        },
        {
          path: 'menu',
          children: [
            { element: <Navigate to="/dashboard/cliente/list" replace />, index: true },
            { path: 'select/:rut', element: <MenuPage /> },
          ],
        },
        {
          path: 'cliente',
          children: [
            { element: <Navigate to="/dashboard/cliente/list" replace />, index: true },
            { path: 'list', element: <ClientList /> },
            { path: 'new', element: <ClientCreate /> },
            { path: ':rut/edit', element: <ClientCreate /> },
          ],
        },
        {
          path: 'tree',
          children: [
            { element: <Navigate to="/dashboard/tree/list" replace />, index: true },
            { path: 'list', element: <TreeListPage /> },
            // { path: 'new', element: <ClientCreate /> },
            // { path: ':id/edit', element: <ClientCreate /> },
          ],
        },
        {
          path: 'specialty',
          children: [
            { element: <Navigate to="/dashboard/specialty/list" replace />, index: true },
            { path: 'list', element: <SpecialtyList /> },
          ],
        },
        {
          path: 'discipline',
          children: [
            { element: <Navigate to="/dashboard/discipline/list" replace />, index: true },
            { path: 'list', element: <DisciplineList /> },
          ],
        },
        {
          path: 'failure',
          children: [
            { element: <Navigate to="/dashboard/failure/list" replace />, index: true },
            { path: 'list', element: <FailureList /> },
          ],
        },
        {
          path: 'area',
          children: [
            { element: <Navigate to="/dashboard/area/list" replace />, index: true },
            { path: 'list', element: <AreaListPage /> },
          ],
        },
        {
          path: 'company',
          children: [
            { element: <Navigate to="/dashboard/company/list" replace />, index: true },
            { path: 'list', element: <EmpresaListPage /> },
          ],
        },
        {
          path: 'division',
          children: [
            { element: <Navigate to="/dashboard/division/list" replace />, index: true },
            { path: 'list', element: <DivisionListPage /> },
          ],
        },
        {
          path: 'place',
          children: [
            { element: <Navigate to="/dashboard/place/list" replace />, index: true },
            { path: 'list', element: <PlaceListPage /> },
          ],
        },
        {
          path: 'location',
          children: [
            { element: <Navigate to="/dashboard/location/list" replace />, index: true },
            { path: 'list', element: <LocationListPage /> },
          ],
        },
        {
          path: 'order',
          children: [
            { element: <Navigate to="/dashboard/order/list" replace />, index: true },
            { path: 'list', element: <OrderListPage /> },
            { path: 'new', element: <OrderCreateInitPage /> },
            { path: ':id/view', element: <OrderCreateInitPage /> },
            { path: ':id/edit', element: <OrderCreate /> },
          ],
        },
        {
          path: 'orderQr',
          children: [
            { element: <Navigate to="/dashboard/orderQr/list" replace />, index: true },
            { path: 'list', element: <OrderQrListPage /> },
            // { path: 'new', element: <OrderQrCreate /> },
            { path: ':id/view', element: <OrderCreateInitPage /> },
            { path: ':id/edit', element: <OrderCreate /> },
          ],
        },
        {
          path: 'ordersReports',
          children: [
            { element: <Navigate to="/dashboard/orderReports" replace />, index: true },
            { path: 'listHistoric', element: <OrdersReportsListHistoric /> },
          ],
        },
        {
          path: 'ordersDashboard',
          children: [
            { element: <Navigate to="/dashboard/ordersDashboard" replace />, index: true },
            { path: 'listTotals', element: <OrdersDashboard /> },
          ],
        },
        {
          path: 'planning',
          children: [
            { element: <Navigate to="/dashboard/planning/list" replace />, index: true },
            { path: 'list', element: <OrderListPage /> },
            { path: ':id/to-plan', element: <PlanningCalendarOrder /> },
            { path: 'calendar', element: <PlanningCalendar /> },
          ],
        },
        {
          path: 'editplace',
          children: [
            { element: <Navigate to="/dashboard/editplace/form" replace />, index: true },
            { path: 'form', element: <TreeEditDashboard /> },
            // { path: ':id/to-plan', element: <PlanningCalendarOrder /> },
            // { path: 'calendar', element: <PlanningCalendar /> },
          ],
        },
        {
          path: 'preventive-maintenance',
          children: [
            { element: <Navigate to="/dashboard/preventive-maintenance/list" replace />, index: true },
            { path: 'list', element: <PreventiveMaintenanceList /> },
            { path: 'new', element: <PreventiveMaintenanceCreateForm /> },
          ],
        },
        {
          path: 'wallpaper',
          children: [
            { element: <Navigate to="/dashboard/wallpaper/list" replace />, index: true },
            { path: 'list', element: <WallpaperListPage /> },
          ],
        },
        {
          path: 'notifications',
          children: [
            { element: <Navigate to="/dashboard/notifications/list" replace />, index: true },
            { path: 'list', element: <NotificationsListPage /> },
            // { path: 'new', element: <PreventiveMaintenanceCreateForm /> },
          ],
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: 'congratz', element: <PageCongratz /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    // {
    //   path: '/',
    //   element: <MainLayout />,
    //   children: [
    //     { element: <HomePage />, index: true },
    //     { path: 'about-us', element: <About /> },
    //     { path: 'contact-us', element: <Contact /> },
    //     { path: 'faqs', element: <Faqs /> },
    //   ],
    // },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const GeneralEcommerce = Loadable(lazy(() => import('../pages/dashboard/GeneralEcommerce')));
const GeneralAnalytics = Loadable(lazy(() => import('../pages/dashboard/GeneralAnalytics')));
const GeneralBanking = Loadable(lazy(() => import('../pages/dashboard/GeneralBanking')));
const GeneralBooking = Loadable(lazy(() => import('../pages/dashboard/GeneralBooking')));

// ECOMMERCE
const EcommerceShop = Loadable(lazy(() => import('../pages/dashboard/EcommerceShop')));
const EcommerceProductDetails = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductDetails')));
const EcommerceProductList = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductList')));
const EcommerceProductCreate = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductCreate')));
const EcommerceProductEdit = Loadable(lazy(() => import('../pages/dashboard/EcommerceProductEdit')));
const EcommerceCheckout = Loadable(lazy(() => import('../pages/dashboard/EcommerceCheckout')));

// INVOICE
const InvoiceList = Loadable(lazy(() => import('../pages/dashboard/InvoiceList')));
const InvoiceDetails = Loadable(lazy(() => import('../pages/dashboard/InvoiceDetails')));
const InvoiceCreate = Loadable(lazy(() => import('../pages/dashboard/InvoiceCreate')));
const InvoiceEdit = Loadable(lazy(() => import('../pages/dashboard/InvoiceEdit')));

// BLOG
const BlogPosts = Loadable(lazy(() => import('../pages/dashboard/BlogPosts')));
const BlogPost = Loadable(lazy(() => import('../pages/dashboard/BlogPost')));
const BlogNewPost = Loadable(lazy(() => import('../pages/dashboard/BlogNewPost')));

// USER
const UserProfile = Loadable(lazy(() => import('../pages/dashboard/UserProfile')));
const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));
const UserList = Loadable(lazy(() => import('../pages/dashboard/UserList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../pages/dashboard/UserCreate')));

// CONFIG
const GlobalConfig = Loadable(lazy(() => import('../pages/dashboard/GlobalConfig')));
// const UserCards = Loadable(lazy(() => import('../pages/dashboard/UserCards')));

// APP
const Chat = Loadable(lazy(() => import('../pages/dashboard/Chat')));
const Mail = Loadable(lazy(() => import('../pages/dashboard/Mail')));
const Calendar = Loadable(lazy(() => import('../pages/dashboard/Calendar')));
const Kanban = Loadable(lazy(() => import('../pages/dashboard/Kanban')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// MAIN
const HomePage = Loadable(lazy(() => import('../pages/Home')));
const About = Loadable(lazy(() => import('../pages/About')));
const Contact = Loadable(lazy(() => import('../pages/Contact')));
const Faqs = Loadable(lazy(() => import('../pages/Faqs')));
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
const PageCongratz = Loadable(lazy(() => import('../pages/PageCongratz')));

// CLIENTES
const ClientList = Loadable(lazy(() => import('../pages/clients/ClientList')));
const ClientCreate = Loadable(lazy(() => import('../pages/clients/ClientCreate')));

// MENU
const MenuPage = Loadable(lazy(() => import('../pages/menus/MenuPage')));

// Mantenedores
const SpecialtyList = Loadable(lazy(() => import('../pages/specialties/SpecialtyList')));
const FailureList = Loadable(lazy(() => import('../pages/failures/FailureList')));
const DisciplineList = Loadable(lazy(() => import('../pages/disciplines/DisciplineList')));

// TREE
const TreeListPage = Loadable(lazy(() => import('../pages/trees/TreeListPage')));

// PLANNING
const PlanningList = Loadable(lazy(() => import('../pages/planning/PlanningList')));
const PlanningCalendarOrder = Loadable(lazy(() => import('../pages/planning/PlanningCalendarOrder')));
const PlanningCalendar = Loadable(lazy(() => import('../pages/planning/PlanningCalendar')));

// PROFILES
const ProfileList = Loadable(lazy(() => import('../pages/profiles/ProfileList')));
const ProfileCreate = Loadable(lazy(() => import('../pages/profiles/ProfileCreate')));

// (DIVISIONES)
const DivisionListPage = Loadable(lazy(() => import('../pages/division/DivisionListPage')));

// AREAS (SUB-DIVISIONES)
const AreaListPage = Loadable(lazy(() => import('../pages/areas/AreaListPage')));

// AREAS (SUB-DIVISIONES)
const EmpresaListPage = Loadable(lazy(() => import('../pages/empresas/EmpresasListPage')));

// LUGARES (PLACES)
const PlaceListPage = Loadable(lazy(() => import('../pages/places/PlaceListPage')));

// UBICACIONES (LOCATIONS)
const LocationListPage = Loadable(lazy(() => import('../pages/locations/LocationListPage')));

// ORDERS
const OrderListPage = Loadable(lazy(() => import('../pages/orders/OrderListPage')));
const OrderCreate = Loadable(lazy(() => import('../pages/orders/OrderCreate')));
const OrderCreateInitPage = Loadable(lazy(() => import('../pages/orders/OrderCreateInitPage')));

// ORDERS QR
const OrderQrListPage = Loadable(lazy(() => import('../pages/ordersQr/OrderQrListPage')));
const OrderQrCreate = Loadable(lazy(() => import('../pages/ordersQr/OrderQrCreate')));
const OrderQrCreateInitPage = Loadable(lazy(() => import('../pages/orders/OrderCreateInitPage')));
// QR-ORDER-FORM
const QROrderForm = Loadable(lazy(() => import('../pages/QROrderForm/OrderCreateInitPage')));
const QROrderFormPlaces = Loadable(lazy(() => import('../pages/QROrderFormPlaces/OrderCreateInitPage')));

// MANTENCIONES PREVENTIVAS
const PreventiveMaintenanceList = Loadable(
  lazy(() => import('../pages/preventive-maintenance/PreventiveMaintenanceList'))
);
const PreventiveMaintenanceCreateForm = Loadable(
  lazy(() => import('../pages/preventive-maintenance/PreventiveMaintenanceCreateForm'))
);

// ORDERS-REPORTS
const OrdersReportsListHistoric = Loadable(lazy(() => import('../pages/ordersReports/ListHistoric')));

// ORDERS-DASHBOARDS
const OrdersDashboard = Loadable(lazy(() => import('../pages/dashboard/ordersDashboard/ordersDashboard')));

// WALLPAPERS
const WallpaperListPage = Loadable(lazy(() => import('../pages/wallpapers/WallpaperListPage')));

// EDITAR ARBOLES
const TreeEditDashboard = Loadable(lazy(() => import('../pages/treeedit/TreeEdit')));

// NOTIFICATIONS
const NotificationsListPage = Loadable(lazy(() => import('../pages/notifications/NotificationList')));

// const OrdersReportsListDynamic = Loadable(lazy(() => import('../pages/ordersReports/ListDynamic')));
// const OrdersReportsListTotals = Loadable(lazy(() => import('../pages/ordersReports/ListTotals')));
